/**
 * Custom hook for managing contribution functionality
 */

import { useState, useContext, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { AuctionContext } from '../context/AuctionContext';
import { useWallet } from '@solana/wallet-adapter-react';

/**
 * Hook for managing contribution logic
 * @returns {Object} Contribution state and handlers
 */
export const useContribution = () => {
  const [amount, setAmount] = useState('');
  const [contributionType, setContributionType] = useState('both');
  
  const { 
    handleContribute, 
    handleReserveAllDays, 
    calculateProjectedStats,
    clearProjectedContributions,
    projectedContributions
  } = useContext(AuctionContext);
  
  const wallet = useWallet();
  const toast = useToast();
  
  /**
   * Handle amount input change
   */
  const handleAmountChange = useCallback((valueString) => {
    setAmount(valueString);
    // If there's a value, calculate projected stats
    if (valueString && parseFloat(valueString) > 0) {
      calculateProjectedStats(parseFloat(valueString));
    } else {
      clearProjectedContributions();
    }
  }, [calculateProjectedStats, clearProjectedContributions]);
  
  /**
   * Handle preset contribution button click
   */
  const handlePresetContribution = useCallback(async (presetAmount) => {
    if (!wallet.connected) return;
    
    setAmount(presetAmount);
    calculateProjectedStats(presetAmount);
  }, [wallet.connected, calculateProjectedStats]);
  
  /**
   * Handle contribution type selection
   */
  const handleContributionTypeSelect = useCallback((type) => {
    setContributionType(type);
  }, []);
  
  /**
   * Handle day click for contribution
   */
  const handleDayClick = useCallback(async (day) => {
    if (!amount || !wallet.connected) return;
    
    const success = await handleContribute(amount, day, 'manual');
    if (success) {
      setAmount('');
      toast({
        title: 'Contribution successful',
        description: `Successfully contributed ${amount} SOL to day ${day}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [amount, wallet.connected, handleContribute, toast]);
  
  /**
   * Handle contribution to all days
   */
  const handleAllDaysContribution = useCallback(async () => {
    if (!amount || !wallet.connected) return;
    
    const success = await handleReserveAllDays(amount);
    if (success) {
      setAmount('');
      toast({
        title: 'Contribution successful',
        description: `Successfully contributed ${amount} SOL to all remaining days`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [amount, wallet.connected, handleReserveAllDays, toast]);
  
  /**
   * Handle claiming rewards
   */
  const handleClaim = useCallback(async () => {
    toast({
      title: 'Claim functionality',
      description: 'Claim functionality will be available after the auction ends',
      status: 'info',
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);
  
  return {
    amount,
    contributionType,
    handleAmountChange,
    handlePresetContribution,
    handleContributionTypeSelect,
    handleDayClick,
    handleAllDaysContribution,
    handleClaim,
    projectedContributions
  };
};
